import React, { useState } from "react";

/**
 * Shared click-to-sort table helpers (backlog #40). Extracted from the staff
 * Users page so every grid can reuse the same behavior + look. Dark-mode aware.
 *
 * Usage:
 *   const { sort, toggle } = useSort("name");
 *   const rows = sortRows(data, sort, (r, key) => key === "name" ? r.name : r.id);
 *   <SortableTh label="Name" sortKey="name" sort={sort} onSort={toggle} />
 */

export type SortState = { key: string; dir: "asc" | "desc" };

export function useSort(defaultKey: string, defaultDir: "asc" | "desc" = "asc") {
  const [sort, setSort] = useState<SortState>({ key: defaultKey, dir: defaultDir });
  const toggle = (key: string) =>
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" },
    );
  return { sort, toggle, setSort };
}

export function sortRows<T>(
  rows: T[],
  sort: SortState,
  valueOf: (row: T, key: string) => string | number,
): T[] {
  const dir = sort.dir === "asc" ? 1 : -1;
  // valueOf is computed ONCE per row, not twice per comparison. Several
  // callers do real work in there (lowercasing, joining a licence list,
  // formatting a date), and a comparison-time call means ~2n·log n of it:
  // at 10k rows that is ~300k invocations to sort a table showing 100.
  // Sorting the decorated pairs keeps the result identical for any valueOf
  // that depends only on the row, which is what every caller does.
  const keyed = rows.map((row) => ({ row, key: valueOf(row, sort.key) }));
  keyed.sort((a, b) => {
    if (a.key < b.key) return -dir;
    if (a.key > b.key) return dir;
    return 0;
  });
  return keyed.map((k) => k.row);
}

export function SortableTh({
  label,
  sortKey,
  sort,
  onSort,
  align = "left",
  hide = "",
  width = "",
}: {
  label: string;
  sortKey: string;
  sort: SortState;
  onSort: (k: string) => void;
  align?: "left" | "right";
  /**
   * Responsive visibility, as Tailwind classes: e.g. "hidden md:table-cell".
   *
   * A data table on a phone should show fewer columns, not scroll sideways -
   * All Devices was 1223px wide in a 366px container with Status ~750px off
   * screen.
   *
   * THE MATCHING <td> MUST CARRY THE SAME STRING. A header hidden while its
   * cell is not shifts every column after it and shows values under the wrong
   * headings, which is worse than the scrolling it replaced. Raw classes on
   * both sides rather than an abstraction here and classes there, so the two
   * are comparable by eye and by grep.
   *
   * Also remember any colSpan in the same table: it is an ATTRIBUTE, so no
   * media query reaches it. See hooks/useVisibleColumns.
   */
  hide?: string;
  /**
   * Column width, as a Tailwind class: e.g. "w-[11rem]".
   *
   * Only meaningful on a `table-fixed` table, which is the point of it. With
   * the default auto layout a table inside an overflow-x container expands to
   * its content's min-content width no matter what the cells ask for, so a
   * long unbreakable message pushes the last columns off the right edge and
   * the only hint is a scrollbar below the fold. table-fixed makes THESE the
   * authority, and columns given no width share what is left.
   */
  width?: string;
}) {
  const active = sort.key === sortKey;
  return (
    <th
      className={`px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase ${
        align === "right" ? "text-right" : "text-left"
      } ${hide} ${width}`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="group inline-flex items-center gap-1 hover:text-gray-800 dark:hover:text-gray-100 transition-colors uppercase"
        title={`Sort by ${label}`}
      >
        {label}
        <span
          className={
            active
              ? "text-gray-700 dark:text-gray-200"
              : "text-gray-300 dark:text-gray-600 group-hover:text-gray-400"
          }
        >
          {active ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </th>
  );
}
